import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Moment from 'react-moment';
import { Tooltip } from '@mui/material';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Info, Trash2, Undo2 } from 'lucide-react';
import Loading from '../../../common/loading';
import Table from '../../../common/table';
import l from '../../../../lang';
import TopDashboardSection from '../../../common/top-dashboard-section';
import Card from '../../../common/card';
import CardDescription from '../../../common/card-description';
import { Button } from '../../../common/button';
import { Urls } from '../../../../config/urls';
import { Routes } from '../../../../config/routes';
import { createDate, getMappedCountryCode } from '../../../../utils/index';
import { usePayouts } from '../../../../store/payouts';
import { useDashboard } from '../../../../store/dashboard';
import { Filters } from '../../../../store/interfaces';
import { PayoutContactListItem } from '../../../../store/payouts/interfaces';
import { ReactComponent as CopyIcon } from '../../../../assets/icons/copy.svg';
import ExportPayoutContacts from './export';
import TransactionStatusIcon from '../../../common/transaction-status-icon';
import ButtonMoreMenu from '../../../common/button-more-menu';
import PayoutContactsFilter from './filter';
import { Empty, EmptyNoFound } from './empty';
import RevalidateIdentityDialog from './revalidate-dialog';
import PayoutCreatedDialog from './payout-created-dialog';
import './styles.scss';

interface PayoutCreatedLocationState {
  payoutCreated?: {
    beneficiaryName?: string;
    bankAccountId?: number;
  };
}

const initFilters: Filters = { search: '', country: '' };

const CONTACT_STATUS_ICON: Record<string, string> = {
  PENDING: 'PENDING',
  PROCESSING: 'PENDING',
  REJECTED: 'DECLINED',
  VERIFIED: 'COMPLETED',
  EXPIRED: 'EXPIRED',
  BLOCKED: 'DECLINED',
};

const onDeleteContact = (_contact: PayoutContactListItem) => undefined;

const copyText = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textArea);

  if (!copied) {
    throw new Error('Unable to copy validation link');
  }
};

const renderOptionalDate = (value?: string) => {
  if (!value) {
    return <span>-</span>;
  }

  return <Moment format="DD/MM/YYYY">{createDate(value)}</Moment>;
};

const PayoutContacts = () => {
  const history = useHistory();
  const { contactsList, getPayoutContactsList, getPayoutContactValidationLink } = usePayouts();
  const { merchantMe } = useDashboard();
  const [filter, setFilter] = useState<Filters>(initFilters);
  const [copiedContactId, setCopiedContactId] = useState<number | null>(null);
  const [copiedValidationLinkId, setCopiedValidationLinkId] = useState<number | null>(null);
  const [requestingValidationLinkId, setRequestingValidationLinkId] = useState<number | null>(null);
  const [contactToRevalidate, setContactToRevalidate] = useState<PayoutContactListItem | null>(null);
  const createdPayout = (history.location.state as PayoutCreatedLocationState | undefined)?.payoutCreated;
  const payoutContactOwner = contactsList.data.find(contact =>
    contact.bank_accounts?.some(account => account.id === createdPayout?.bankAccountId)
  );
  const showPayoutCreated = Boolean(
    createdPayout && !contactsList.loading && payoutContactOwner && payoutContactOwner.status.toUpperCase() !== 'VERIFIED'
  );
  const { loading, error, data } = contactsList;
  const onAddContact = () => history.push(Routes.PAYOUT_CONTACT_CREATE);
  const [showPayoutCreatedDialog, setShowPayoutCreatedDialog] = useState(false);

  useEffect(() => {
    if (merchantMe?.id) {
      getPayoutContactsList(String(merchantMe.id));
    }
  }, [merchantMe?.id]);

  useEffect(() => {
    if (showPayoutCreated) {
      setShowPayoutCreatedDialog(true);
    }
  }, [showPayoutCreated]);

  const hasFilter = Boolean(filter.search || filter.country);
  const rows = useMemo(() => {
    const search = (filter.search || '').trim().toLowerCase();
    const countries = filter.country ? filter.country.split(',') : [];

    return data.filter(contact => {
      const name = `${contact.first_name} ${contact.last_name}`.toLowerCase();
      const email = (contact.email || '').toLowerCase();
      const matchesSearch = !search || name.includes(search) || (Boolean(contact.email) && email.includes(search));
      const matchesCountry = countries.length === 0 || countries.includes(contact.country);
      return matchesSearch && matchesCountry;
    });
  }, [data, filter]);
  const hasRows = rows.length > 0;

  const EmptyBody = () => {
    if (!error && !hasRows && !hasFilter) {
      return <Empty onAddContact={onAddContact} />;
    }
    if (error || !hasRows) {
      return <EmptyNoFound />;
    }
    return null;
  };

  const renderContact = (contact: PayoutContactListItem) => {
    const initial = (contact.first_name || '').trim().charAt(0).toUpperCase();
    const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '-';

    return (
      <div className="payout_contacts__contact">
        <span className="payout_contacts__avatar" aria-hidden>
          {initial}
        </span>
        <div className="payout_contacts__contact-details">
          <span className="payout_contacts__contact-name">{name}</span>
          {contact.email ? (
            <CopyToClipboard text={contact.email} onCopy={() => setCopiedContactId(contact.id)}>
              <Tooltip
                onMouseEnter={() => setCopiedContactId(null)}
                title={l(copiedContactId === contact.id ? 'common.copied' : 'common.copy')}
              >
                <button type="button" className="payout_contacts__email-copy">
                  <CopyIcon className="payout_contacts__email-copy-icon" aria-hidden />
                  <span>{contact.email}</span>
                </button>
              </Tooltip>
            </CopyToClipboard>
          ) : (
            <span>-</span>
          )}
        </div>
      </div>
    );
  };

  const renderStatus = (status?: string) => {
    const normalized = (status || '').toUpperCase();
    const iconStatus = CONTACT_STATUS_ICON[normalized];

    if (!iconStatus) {
      return <span>-</span>;
    }

    return <TransactionStatusIcon status={iconStatus} tooltipTitle={l(`payoutContacts.status.${normalized}`)} />;
  };

  const handleCopyValidationLink = (contact: PayoutContactListItem) => {
    if (!merchantMe?.id || requestingValidationLinkId !== null) {
      return;
    }

    setCopiedValidationLinkId(null);
    setRequestingValidationLinkId(contact.id);
    getPayoutContactValidationLink(
      String(merchantMe.id),
      String(contact.id),
      validationLink => {
        copyText(validationLink)
          .then(() => setCopiedValidationLinkId(contact.id))
          .catch(() => undefined)
          .finally(() => setRequestingValidationLinkId(null));
      },
      () => setRequestingValidationLinkId(null)
    );
  };

  const renderValidationLink = (contact: PayoutContactListItem) => (
    <Tooltip
      onMouseEnter={() => setCopiedValidationLinkId(null)}
      title={l(copiedValidationLinkId === contact.id ? 'common.copied' : 'common.copy')}
    >
      <span>
        <button
          type="button"
          className="payout_contacts__validation-link-copy"
          disabled={!merchantMe?.id || requestingValidationLinkId !== null}
          onClick={() => handleCopyValidationLink(contact)}
        >
          <CopyIcon className="payout_contacts__validation-link-copy-icon" aria-hidden />
          <span>{l('common.copy')}</span>
        </button>
      </span>
    </Tooltip>
  );

  const renderRowActions = (contact: PayoutContactListItem) => (
    <ButtonMoreMenu
      id={`payout-contact-actions-${contact.id}`}
      items={[
        {
          content: (
            <span className="payout_contacts__menu-item">
              <Info size={18} strokeWidth={1.75} aria-hidden />
              {l('payoutContacts.menu.viewDetail')}
            </span>
          ),
          onClick: () => history.push(Routes.PAYOUT_CONTACT_DETAIL.replace(':contact_id', String(contact.id))),
        },
        {
          content: (
            <span className="payout_contacts__menu-item">
              <Trash2 size={18} strokeWidth={1.75} aria-hidden />
              {l('payoutContacts.menu.delete')}
            </span>
          ),
          onClick: () => onDeleteContact(contact),
        },
        {
          content: (
            <span className="payout_contacts__menu-item">
              <Undo2 size={18} strokeWidth={1.75} aria-hidden />
              {l('payoutContacts.menu.revalidate')}
            </span>
          ),
          disabled: !contact.manual_revalidation_eligible,
          onClick: () => setContactToRevalidate(contact),
        },
      ]}
    />
  );

  const renderCountry = (contact: PayoutContactListItem) => (
    <span className="payout_contacts__country">
      <img
        alt=""
        className="payout_contacts__country__flag country-flag--rounded"
        src={`${Urls.roundedFlags.replace(':code', getMappedCountryCode(contact.country))}`}
      />
      <span>{l(`countryCode.${contact.country}`)}</span>
    </span>
  );

  const handleClosePayoutCreatedDialog = () => {
    history.replace(Routes.PAYOUT_CONTACTS);
    setShowPayoutCreatedDialog(false);
  };

  return (
    <div className="payout_contacts">
      <TopDashboardSection title={l('menu.item.PAYOUT_CONTACTS')} subtitle={l('payoutContacts.subtitle')} />
      <Card>
        <CardDescription>
          <div className="payout_contacts__header">
            <span>{l('payoutContacts.desc')}</span>
            <Button variant="contained" color="primary" onClick={onAddContact}>
              + {l('payoutContacts.add')}
            </Button>
          </div>
        </CardDescription>
        <div className="payout_contacts__content">
          <PayoutContactsFilter
            loading={loading}
            appliedFilters={filter}
            changeFilters={setFilter}
            downloadButton={<ExportPayoutContacts />}
          />
          {loading && (
            <div className="payout_contacts__loading">
              <Loading />
            </div>
          )}
          {!loading && (
            <div className="payout_contacts__table">
              <Table
                withBtnMenu
                emptyBody={<EmptyBody />}
                headers={[
                  <span className="payout_contacts__column__left">{l('payoutContacts.column.contact')}</span>,
                  <span className="payout_contacts__column__left">{l('payoutContacts.column.createdAt')}</span>,
                  <span className="payout_contacts__column__left">{l('payoutContacts.column.expiresAt')}</span>,
                  <span className="payout_contacts__column__left">{l('payoutContacts.column.country')}</span>,
                  <span className="payout_contacts__column__left">{l('payoutContacts.column.verificationLink')}</span>,
                  <span className="payout_contacts__column__left">{l('payoutContacts.column.verificationStatus')}</span>,
                ]}
                body={
                  (hasRows &&
                    !error &&
                    rows.map(contact => [
                      renderContact(contact),
                      <span className="payout_contacts__column__left">{renderOptionalDate(contact.created_at)}</span>,
                      <span className="payout_contacts__column__left">{renderOptionalDate(contact.expires_at)}</span>,
                      renderCountry(contact),
                      renderValidationLink(contact),
                      renderStatus(contact.status),
                      renderRowActions(contact),
                    ])) ||
                  null
                }
              />
            </div>
          )}
        </div>
      </Card>
      <RevalidateIdentityDialog
        open={Boolean(contactToRevalidate)}
        contact={contactToRevalidate}
        beneficiaryId={contactToRevalidate?.id ?? 0}
        onClose={() => setContactToRevalidate(null)}
      />
      <PayoutCreatedDialog
        open={showPayoutCreatedDialog}
        beneficiaryName={createdPayout?.beneficiaryName || '-'}
        onClose={handleClosePayoutCreatedDialog}
      />
    </div>
  );
};

export default PayoutContacts;
